import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { FormSection } from '../../components/ui/FormSection';
import { NicknameField } from '../../components/integrations/NicknameField';
import { resolveNickname } from '../../lib/mappingNickname';
import { TextField } from '../../components/ui/TextField';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Toast } from '../../components/ui/Toast';
import { UpgradePrompt } from '../../components/UpgradePrompt';
import { uid, type Mapping } from '../../types';
import { LimitError, fetchSalesforceConnectUrl } from '../../api/dataApi';

// Salesforce fast path (Phase 4 of docs/SIGNAL_TEMPLATES_AS_MAPPING_SCHEMA_
// PLAN.md): name -> channel -> object type -> create -> "Connect to
// Salesforce" (OAuth) -> wait for the next poll tick. Unlike GitHub/Stripe
// (a webhook this app receives), Salesforce is polled: internal/webhookrecv/
// salesforce_poll.go's cron tick runs every ~5 minutes for every connected
// mapping - there's no per-mapping interval to configure, so this screen
// doesn't offer one rather than promise something the backend doesn't
// actually read.
const OBJECT_TYPES = ['Opportunity', 'Lead', 'Case'] as const;

export function SalesforceSetupScreen() {
  const navigate = useNavigate();
  const { channels, isChannelOwner, addMapping } = useStore();
  const ownedChannels = channels.filter(isChannelOwner);

  const [title, setTitle] = useState('');
  const [nicknameInput, setNicknameInput] = useState('');
  const [channelNickname, setChannelNickname] = useState(ownedChannels[0]?.nickname ?? '');
  const [objectType, setObjectType] = useState<(typeof OBJECT_TYPES)[number]>('Opportunity');
  const [creating, setCreating] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');
  const [limitMessage, setLimitMessage] = useState('');
  const [created, setCreated] = useState<Mapping | null>(null);

  const canCreate = title.trim() !== '' && channelNickname.trim() !== '' && !creating;

  const create = async () => {
    if (!canCreate) return;
    setCreating(true);
    setError('');
    try {
      const payload: Mapping = {
        id: uid(),
        nickname: resolveNickname(nicknameInput, '@salesforce', uid().slice(0, 8)),
        title: title.trim(),
        description: `Salesforce ${objectType} -> #${channelNickname}`,
        isPublic: false,
        sourceType: 'salesforce',
        spec: { channel: channelNickname, objectType },
      };
      const saved = await addMapping(payload);
      setCreated(saved);
    } catch (err) {
      if (err instanceof LimitError) {
        setLimitMessage(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Unable to create this integration.');
      }
    } finally {
      setCreating(false);
    }
  };

  const connect = async () => {
    if (!created) return;
    setConnecting(true);
    setError('');
    try {
      const { url, state } = await fetchSalesforceConnectUrl(created.nickname);
      // Read back by SalesforceOAuthCallbackScreen once Salesforce redirects
      // to /oauth/callback/salesforce - see that screen's own comment for
      // why the mapping id (not just its nickname) needs to survive the
      // round trip client-side.
      sessionStorage.setItem('oauth.sf.state', state);
      sessionStorage.setItem('oauth.sf.mappingId', created.id);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to start the Salesforce connection.');
      setConnecting(false);
    }
  };

  if (created) {
    return (
      <div className="flex flex-col gap-4">
        <ScreenHeader title="Connect Salesforce" backTo="/integrations/catalog" />

        <FormSection title="Authorize this org">
          <p className="text-sm text-black/60 dark:text-white/60">
            You'll be sent to Salesforce to sign in and approve access, then brought back here automatically. Zmist
            only ever reads {objectType} records via SOQL - nothing is written back to your org.
          </p>
          <Button onClick={connect} loading={connecting} full>
            <ExternalLink size={16} />
            Connect to Salesforce
          </Button>
        </FormSection>

        <FormSection title="What happens next">
          <p className="text-sm text-black/60 dark:text-white/60">
            Once connected, Zmist polls this org's {objectType} records roughly every 5 minutes and maps each one to
            a Signal (one signal per record, updated as it changes) in the destination channel. There's no live
            delivery status yet - check the destination channel after a few minutes for the first Signal.
          </p>
        </FormSection>

        <Button variant="secondary" onClick={() => navigate(`/integrations/${created.id}`)} full>
          Go to integration
        </Button>

        {error && <Toast message={error} onDismiss={() => setError('')} />}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <ScreenHeader title="Connect Salesforce" backTo="/integrations/catalog" />

      <FormSection title="Name this integration">
        <TextField
          label="Name"
          placeholder="Salesforce pipeline"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <NicknameField value={nicknameInput} onChange={setNicknameInput} example="@salesforce" />
      </FormSection>

      <FormSection title="Object to poll">
        <Select label="Object" value={objectType} onChange={(e) => setObjectType(e.target.value as typeof objectType)}>
          {OBJECT_TYPES.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </Select>
      </FormSection>

      <FormSection title="Destination channel">
        {ownedChannels.length === 0 ? (
          <p className="text-sm text-black/60 dark:text-white/60">
            You need to own at least one channel before connecting an integration - create a channel first, then come
            back here.
          </p>
        ) : (
          <Select label="Channel" value={channelNickname} onChange={(e) => setChannelNickname(e.target.value)}>
            {ownedChannels.map((c) => (
              <option key={c.id} value={c.nickname}>
                {c.title} ({c.nickname})
              </option>
            ))}
          </Select>
        )}
      </FormSection>

      <Button onClick={create} loading={creating} disabled={!canCreate || ownedChannels.length === 0} full>
        Continue
      </Button>

      {error && <Toast message={error} onDismiss={() => setError('')} />}
      <UpgradePrompt
        open={!!limitMessage}
        onClose={() => setLimitMessage('')}
        message={limitMessage}
        title="Connector limit reached"
      />
    </div>
  );
}
